/**
 * Iter XX.221 — Connect Your Cricket modal flow.
 *
 * Three steps:
 *   1. Paste URL (with provider list showing which are supported).
 *   2. "Is this you?" confirmation — displays ONLY what the URL
 *      itself revealed (never a fabricated player name).
 *   3. Success — routes to either "Show me what you notice"
 *      (≥3 innings on record) OR "Add a scorecard" (no innings yet).
 *
 * All copy is deliberately honest — CricHeroes URL capture is not
 * pretending to import matches.
 */
import { useEffect, useState } from "react";
import { X, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import {
  fetchProviders, previewProfile, confirmProfile,
} from "../lib/connectCricket";


export default function ConnectCricketModal({ open, onClose, onSuccess }) {
  const [providers, setProviders] = useState([]);
  const [url, setUrl] = useState("");
  const [step, setStep] = useState("paste");   // paste | confirm | success | error
  const [preview, setPreview] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    fetchProviders().then((p) => setProviders(p || []));
    return () => {
      setStep("paste"); setUrl(""); setPreview(null);
      setConfirmResult(null); setErr("");
    };
  }, [open]);

  if (!open) return null;

  const handlePreview = async (e) => {
    e?.preventDefault?.();
    setErr(""); setBusy(true);
    try {
      const r = await previewProfile(url.trim());
      if (!r?.resolved) {
        setErr(
          "That doesn't look like a supported profile URL yet. " +
          "CricHeroes profile links work today.",
        );
        setBusy(false);
        return;
      }
      setPreview(r);
      setStep("confirm");
    } catch (e2) {
      setErr(String(e2?.message || e2));
    } finally { setBusy(false); }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setBusy(true); setErr("");
    try {
      const r = await confirmProfile({
        url: preview.profile_url,
        provider: preview.provider,
        external_player_id: preview.external_player_id,
      });
      setConfirmResult(r);
      setStep("success");
    } catch (e2) {
      setErr(String(e2?.message || e2));
      setStep("error");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="connect-cricket-modal"
      onClick={onClose}
    >
      <div
        className="bg-neutral-900 border border-neutral-700 rounded-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-500 hover:text-white"
          data-testid="connect-cricket-modal-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {step === "paste" && (
          <>
            <h2 className="text-xl font-semibold text-white mb-1"
                 data-testid="connect-cricket-modal-title">
              🏏 Connect Your Cricket
            </h2>
            <p className="text-sm text-neutral-400 mb-4">
              Paste your cricket player-profile link.
            </p>
            <form onSubmit={handlePreview}>
              <input
                type="url" value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://cricheroes.com/player-profile/…"
                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                data-testid="connect-cricket-url-input"
                required minLength={4}
              />
              {err && (
                <p className="mt-2 text-xs text-red-400"
                    data-testid="connect-cricket-error">{err}</p>
              )}
              <button
                type="submit"
                disabled={busy || !url.trim()}
                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2"
                data-testid="connect-cricket-preview-btn"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Look up profile
              </button>
            </form>

            {providers.length > 0 && (
              <div className="mt-6 pt-4 border-t border-neutral-800">
                <p className="text-xs text-neutral-500 mb-2">Supported providers</p>
                <ul className="text-xs space-y-1"
                    data-testid="connect-cricket-provider-list">
                  {providers.map((p) => (
                    <li key={p.provider}
                        className="flex items-center justify-between text-neutral-300">
                      <span>{p.label}</span>
                      <span className={`text-[10px] uppercase tracking-wider ${
                        p.status === "supported" ? "text-emerald-400" : "text-neutral-500"
                      }`}
                            data-testid={`connect-cricket-provider-${p.provider}-status`}>
                        {p.status === "supported" ? "Available" : "Coming soon"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {step === "confirm" && preview && (
          <>
            <h2 className="text-xl font-semibold text-white mb-1"
                 data-testid="connect-cricket-confirm-title">
              Is this you?
            </h2>
            <p className="text-sm text-neutral-400 mb-4">
              We'll only link your profile after you confirm.
            </p>
            <div className="bg-neutral-800 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-neutral-500">Provider</span>
                <span className="text-white capitalize"
                       data-testid="connect-cricket-confirm-provider">
                  {preview.provider}
                </span>
              </div>
              {preview.external_player_id && (
                <div className="flex items-center justify-between">
                  <span className="text-neutral-500">Player ID</span>
                  <span className="text-white font-mono text-xs"
                         data-testid="connect-cricket-confirm-player-id">
                    {preview.external_player_id}
                  </span>
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <span className="text-neutral-500">Profile</span>
                <a href={preview.profile_url} target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:text-emerald-300 text-xs truncate max-w-[200px] flex items-center gap-1"
                    data-testid="connect-cricket-confirm-profile-url">
                  Open <ExternalLink size={11} />
                </a>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
              CricHeroes doesn't allow automatic match sync yet — after
              you link your profile, upload a scorecard to start
              building your cricket record with Gully.
            </p>
            {err && (
              <p className="mt-2 text-xs text-red-400"
                  data-testid="connect-cricket-error">{err}</p>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setStep("paste")}
                className="bg-neutral-800 hover:bg-neutral-700 text-white text-sm py-2 rounded-lg"
                data-testid="connect-cricket-not-me-btn"
              >
                That's not me
              </button>
              <button
                onClick={handleConfirm} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm py-2 rounded-lg flex items-center justify-center gap-2"
                data-testid="connect-cricket-confirm-btn"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Yes, that's me
              </button>
            </div>
          </>
        )}

        {step === "success" && confirmResult && (
          <div data-testid="connect-cricket-success">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="text-emerald-500" size={22} />
              <h2 className="text-xl font-semibold text-white">
                Profile linked
              </h2>
            </div>
            <p className="text-sm text-neutral-400 mb-4">
              Talk to me about your cricket or upload a scorecard —
              that's how I'll start knowing your game.
            </p>
            <button
              onClick={() => {
                onSuccess?.(confirmResult);
                onClose?.();
              }}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium py-2.5 rounded-lg"
              data-testid={
                confirmResult?.next_action?.kind === "show_observation"
                  ? "connect-cricket-success-observe"
                  : "connect-cricket-success-add-scorecard"
              }
            >
              {confirmResult?.next_action?.kind === "show_observation"
                ? "Show me what you notice"
                : "Add a scorecard"}
            </button>
            <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
              {confirmResult?.next_action?.kind === "show_observation"
                ? "I've been listening — here's what stands out from your recent innings."
                : "Automatic sync with CricHeroes isn't available yet. Talk to me about matches, upload scorecards, or add innings manually — your cricket record grows either way."}
            </p>
          </div>
        )}

        {step === "error" && (
          <div data-testid="connect-cricket-error-step">
            <h2 className="text-lg font-semibold text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-red-400">{err}</p>
            <button
              onClick={() => setStep("paste")}
              className="mt-4 w-full bg-neutral-800 hover:bg-neutral-700 text-white text-sm py-2 rounded-lg"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
